/**
 * PhotoManagementTab — Hotel photo gallery management
 * Upload, reorder, set hero, delete, view AI analysis
 * Renders actual images from Emergent Object Storage
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Camera, Upload, Trash2, Star, Loader2, ImageIcon,
  CheckCircle, AlertTriangle, Eye, RefreshCw,
  Sparkles, ArrowUp, ArrowDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL, API } from '@/lib/backendUrl';


const CATEGORY_COLORS = {
  exterior: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  lobby: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  room: 'bg-violet-500/10 text-violet-400 border-violet-500/20',
  bathroom: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
  restaurant: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  pool: 'bg-sky-500/10 text-sky-400 border-sky-500/20',
  spa: 'bg-pink-500/10 text-pink-400 border-pink-500/20',
  gym: 'bg-red-500/10 text-red-400 border-red-500/20',
  view: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20',
  meeting_room: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  other: 'bg-gray-500/10 text-gray-400 border-gray-500/20',
};

const photoUrl = (path) => path ? `${API}/api/photos/serve/${path}` : null;

export function PhotoManagementTab({ hotelId }) {
  const [photos, setPhotos] = useState([]);
  const [coverage, setCoverage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadCount, setUploadCount] = useState(0);
  const [uploadTotal, setUploadTotal] = useState(0);
  const fileRef = useRef(null);

  const fetchPhotos = useCallback(async () => {
    if (!hotelId) return;
    setLoading(true);
    try {
      const [photosRes, coverageRes] = await Promise.all([
        fetch(`${API}/api/photos/hotel/${hotelId}`).then(r => r.json()),
        fetch(`${API}/api/photos/hotel/${hotelId}/coverage`).then(r => r.json()),
      ]);
      const sorted = (photosRes.photos || []).sort((a, b) => (a.sort_order ?? 99) - (b.sort_order ?? 99));
      setPhotos(sorted);
      setCoverage(coverageRes);
    } catch (err) {
      toast.error('Failed to load photos');
    } finally {
      setLoading(false);
    }
  }, [hotelId]);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setUploadTotal(files.length);
    setUploadCount(0);

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch(`${API}/api/photos/upload/${hotelId}`, {
          method: 'POST',
          body: formData,
        });
        const data = await res.json();
        if (data.success) {
          toast.success(`${data.category} photo uploaded (${data.quality_score}/10)`);
        } else {
          toast.error(data.detail || 'Upload failed');
        }
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
      setUploadCount(prev => prev + 1);
    }

    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
    fetchPhotos();
  };

  const handleDelete = async (photoId) => {
    try {
      await fetch(`${API}/api/photos/hotel/${hotelId}/${photoId}`, { method: 'DELETE' });
      setPhotos(prev => prev.filter(p => p.id !== photoId));
      toast.success('Photo deleted');
      fetchPhotos();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const setAsHero = async (photoId) => {
    try {
      await fetch(`${API}/api/photos/hotel/${hotelId}/set-hero?photo_id=${photoId}`, { method: 'POST' });
      toast.success('Hero image updated');
      fetchPhotos();
    } catch (err) {
      toast.error('Failed to set hero');
    }
  };

  const movePhoto = async (index, direction) => {
    const newPhotos = [...photos];
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= newPhotos.length) return;
    [newPhotos[index], newPhotos[swapIndex]] = [newPhotos[swapIndex], newPhotos[index]];
    setPhotos(newPhotos);
    // Save order
    const ids = newPhotos.map(p => p.id);
    try {
      await fetch(`${API}/api/photos/hotel/${hotelId}/reorder`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ids),
      });
    } catch (err) { /* silently handle */ }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="photos-loading">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="photo-management-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold heading-font mb-1">Hotel Photos</h2>
          <p className="text-muted-foreground text-sm">Managed by Argus — auto-optimized for fast loading</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchPhotos} data-testid="refresh-photos">
            <RefreshCw className="w-3 h-3 mr-1" /> Refresh
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            className="hidden"
            onChange={handleUpload}
          />
          <Button size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-photos-btn">
            {uploading ? (
              <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> {uploadCount}/{uploadTotal}</>
            ) : (
              <><Upload className="w-3 h-3 mr-1" /> Upload Photos</>
            )}
          </Button>
        </div>
      </div>

      {/* Upload progress */}
      {uploading && (
        <Progress value={(uploadCount / uploadTotal) * 100} className="h-1.5" />
      )}

      {/* Coverage */}
      {coverage && (
        <Card data-testid="photo-coverage">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-teal-500" />
                <span className="text-sm font-medium heading-font">Argus Photo Coverage</span>
              </div>
              <Badge variant={coverage.ready ? 'default' : 'secondary'} className="text-xs">
                {coverage.total_photos} photos / {coverage.ready ? 'Ready' : 'Incomplete'}
              </Badge>
            </div>
            <Progress value={coverage.coverage_pct} className="h-1.5 mb-3" />
            <div className="flex flex-wrap gap-1.5 mb-2">
              {coverage.categories_covered?.map(cat => (
                <Badge key={cat} className={`text-[10px] ${CATEGORY_COLORS[cat] || CATEGORY_COLORS.other}`}>
                  <CheckCircle className="w-2.5 h-2.5 mr-1" />{cat}
                </Badge>
              ))}
              {coverage.required_missing?.map(cat => (
                <Badge key={cat} variant="outline" className="text-[10px] border-destructive/30 text-destructive">
                  <AlertTriangle className="w-2.5 h-2.5 mr-1" />{cat} needed
                </Badge>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">{coverage.suggestion}</p>
          </CardContent>
        </Card>
      )}

      {/* Photo Grid */}
      {photos.length === 0 ? (
        <Card className="border-dashed border-2">
          <CardContent className="p-12 text-center">
            <Camera className="w-10 h-10 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground mb-1">No photos yet</p>
            <p className="text-xs text-muted-foreground/60 mb-4">Upload exterior, lobby, and room photos to get started</p>
            <Button onClick={() => fileRef.current?.click()} size="sm" data-testid="upload-first-photo">
              <Upload className="w-3 h-3 mr-1" /> Upload First Photo
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="photo-grid">
          {photos.map((photo, idx) => {
            const isHero = photo.is_hero;
            const thumbPath = photo.variants?.card?.path || photo.variants?.thumbnail?.path;
            const qScore = photo.quality_score || 0;
            const qColor = qScore >= 7 ? 'bg-emerald-500' : qScore >= 4 ? 'bg-amber-500' : 'bg-red-500';
            return (
              <Card key={photo.id} className={`overflow-hidden group ${isHero ? 'ring-2 ring-primary' : ''}`} data-testid={`photo-card-${photo.id}`}>
                <div className="aspect-[4/3] bg-muted/10 relative overflow-hidden">
                  {thumbPath ? (
                    <img
                      src={photoUrl(thumbPath)}
                      alt={photo.description || photo.category}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="w-8 h-8 text-muted-foreground/20" />
                    </div>
                  )}
                  {isHero && (
                    <Badge className="absolute top-2 left-2 bg-primary text-primary-foreground text-[9px]" data-testid="hero-badge">
                      <Star className="w-2.5 h-2.5 mr-0.5 fill-current" /> Hero
                    </Badge>
                  )}
                  <div className={`absolute top-2 right-2 w-5 h-5 ${qColor} rounded-full flex items-center justify-center text-[9px] font-bold text-white shadow`}>
                    {qScore}
                  </div>
                  {/* Hover actions */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1.5 p-2">
                    <div className="flex gap-1.5">
                      {!isHero && (
                        <Button size="sm" variant="secondary" className="h-7 text-[10px]" onClick={() => setAsHero(photo.id)} data-testid={`set-hero-${photo.id}`}>
                          <Star className="w-3 h-3 mr-1" /> Hero
                        </Button>
                      )}
                      <Button size="sm" variant="destructive" className="h-7 text-[10px]" onClick={() => handleDelete(photo.id)} data-testid={`delete-${photo.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-white hover:bg-white/20" onClick={() => movePhoto(idx, -1)} disabled={idx === 0}>
                        <ArrowUp className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-white hover:bg-white/20" onClick={() => movePhoto(idx, 1)} disabled={idx === photos.length - 1}>
                        <ArrowDown className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
                <CardContent className="p-2.5">
                  <div className="flex items-center justify-between">
                    <Badge className={`text-[9px] ${CATEGORY_COLORS[photo.category] || CATEGORY_COLORS.other}`}>
                      {photo.category}
                    </Badge>
                    <span className="text-[9px] text-muted-foreground tabular-nums">
                      {photo.variants?.hero?.size_kb || '?'}KB
                    </span>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1 line-clamp-1">{photo.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Size Optimization Info */}
      <Card className="bg-muted/20">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-xs font-medium heading-font">Auto-Optimization by Argus</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            {[
              { name: 'Hero', size: '1920 x 1080', use: 'Landing, Search' },
              { name: 'Gallery', size: '1200 x 800', use: 'Hotel Detail' },
              { name: 'Card', size: '600 x 400', use: 'Listing Cards' },
              { name: 'Thumb', size: '300 x 200', use: 'Admin, Previews' },
            ].map(s => (
              <div key={s.name}>
                <p className="text-muted-foreground">{s.name}</p>
                <p className="font-medium">{s.size}</p>
                <p className="text-[10px] text-muted-foreground/60">{s.use}</p>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-muted-foreground mt-2">All photos auto-converted to WebP @ 80% quality for fastest loading.</p>
        </CardContent>
      </Card>
    </div>
  );
}
